import { useState, useEffect } from 'react';
import {
  Upload, RefreshCw, ImageIcon, Save, X, Edit, Star,
  Package, Shield, Home, Database, AlertTriangle, Settings,
  Cloud, Zap, Globe, DollarSign
} from 'lucide-react';
import QuickDbSetup from '../components/QuickDbSetup';
import AdminSettings from '../components/AdminSettings';
import firebaseMetadataService from '../services/firebaseMetadataService';
import vercelBlobService from '../services/vercelBlobService';
import VercelBlobUploader from '../components/VercelBlobUploader';

const VercelSuperAdmin = () => {
  // State management
  const [activeTab, setActiveTab] = useState('products');
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [testingFirebase, setTestingFirebase] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [storageStats, setStorageStats] = useState(null);

  // Load images from Vercel Blob on component mount
  useEffect(() => {
    loadImages();
    loadStorageStats();

    // Set up global refresh function for other components to call
    window.refreshAllImages = loadImages;

    // Cleanup on unmount
    return () => {
      delete window.refreshAllImages;
    };
  }, []);

  const loadImages = async () => {
    try {
      setLoadingImages(true);
      console.log('🔄 Loading ALL WEBSITE images from Vercel Blob...');

      // Load images from Vercel Blob
      const blobImages = await vercelBlobService.getProductImages();
      
      if (blobImages && blobImages.length > 0) {
        // Load images with metadata from Firebase (admin-added data only)
        const imagesWithMetadata = await Promise.all(blobImages.map(async (blobImage) => {
          // Extract filename from pathname
          const filename = blobImage.pathname.split('/').pop();

          // Try to load metadata from Firebase
          let metadata = null;
          try {
            metadata = await firebaseMetadataService.getImageMetadata(filename);
          } catch (error) {
            console.log(`No Firebase metadata for ${filename}`);
          }

          // ONLY use admin-added metadata from Firebase - NO automatic generation
          const finalMetadata = metadata ? {
            title: metadata.title || '',
            description: metadata.description || '',
            category: metadata.category || '',
            price: metadata.price || null,
            inStock: metadata.in_stock !== undefined ? metadata.in_stock : false,
            featured: metadata.featured || false,
            hasMetadata: true
          } : {
            // NO automatic data generation - empty metadata for admin to fill
            title: '',
            description: '',
            category: '',
            price: null,
            inStock: false,
            featured: false,
            hasMetadata: false
          };

          return {
            ...blobImage,
            id: filename.replace(/\.[^/.]+$/, ""), // Remove file extension for ID
            name: filename,
            publicUrl: blobImage.url,
            fullPath: blobImage.pathname,
            storage: 'vercel-blob',
            ...finalMetadata
          };
        }));

        setImages(imagesWithMetadata);
        console.log(`✅ Loaded ${imagesWithMetadata.length} WEBSITE images from Vercel Blob`);

        // Count images with and without metadata
        const withMetadata = imagesWithMetadata.filter(img => img.hasMetadata).length;
        const withoutMetadata = imagesWithMetadata.length - withMetadata;
        console.log(`📊 Images with admin metadata: ${withMetadata}, without metadata: ${withoutMetadata}`);
      } else {
        setImages([]);
        console.log('ℹ️ No images found in Vercel Blob storage');
      }
    } catch (error) {
      console.error('❌ Error loading WEBSITE images from Vercel Blob:', error);
    } finally {
      setLoadingImages(false);
    }
  };

  const loadStorageStats = async () => {
    try {
      const blobImages = await vercelBlobService.getProductImages();
      const totalSize = blobImages.reduce((sum, img) => sum + (img.size || 0), 0);
      const totalCount = blobImages.length;
      const withMetadata = images.filter(img => img.hasMetadata).length;
      
      setStorageStats({
        totalImages: totalCount,
        totalSize: totalSize,
        withMetadata: withMetadata,
        withoutMetadata: totalCount - withMetadata,
        averageSize: totalCount > 0 ? totalSize / totalCount : 0
      });
    } catch (error) {
      console.error('Error loading storage stats:', error);
    }
  };

  const testFirebaseConnection = async () => {
    try {
      setTestingFirebase(true);
      console.log('🔄 Testing Firebase connection...');

      await firebaseMetadataService.testConnection();
      alert('✅ Firebase connection test successful! You can save metadata.');
    } catch (error) {
      console.error('❌ Firebase test failed:', error);
      alert(`❌ Firebase connection failed: ${error.message}`);
    } finally {
      setTestingFirebase(false);
    }
  };

  const handleDeleteImage = async (image) => {
    if (!confirm(`Are you sure you want to delete "${image.title || image.name}"? This will remove it from ALL sections of the website (Home, Gallery, Featured products).`)) {
      return;
    }

    try {
      console.log('🗑️ Deleting image from Vercel Blob...');

      await vercelBlobService.deleteFile(image.url);

      console.log('✅ Image deleted successfully from Vercel Blob storage');

      // Trigger refresh across all pages
      await loadImages();
      await loadStorageStats();

      // Trigger refresh on other pages
      if (window.refreshHomeImages) {
        window.refreshHomeImages();
      }
      if (window.refreshGalleryImages) {
        window.refreshGalleryImages();
      }

      // Dispatch global refresh event
      window.dispatchEvent(new CustomEvent('refreshAllImages'));
    } catch (error) {
      console.error('❌ Error deleting image:', error);
      alert('Failed to delete image. Please try again.');
    }
  };

  // Helper function to format bytes
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Cloud className="w-8 h-8 text-green-600 mr-3" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Vercel Super Admin</h1>
                <p className="text-sm text-gray-600">Vercel Blob Storage Management - Global CDN Performance</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-green-50 px-3 py-1 rounded-full">
                <Globe className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-800">100+ Edge Locations</span>
              </div>
              <button
                onClick={() => window.open('/', '_blank')}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center"
              >
                <Home className="w-4 h-4 mr-2" />
                View Website
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('products')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'products'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Package className="w-4 h-4 inline mr-2" />
              Vercel Blob Images
            </button>
            <button
              onClick={() => setActiveTab('upload')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'upload'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Upload className="w-4 h-4 inline mr-2" />
              Upload to Vercel Blob
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Database className="w-4 h-4 inline mr-2" />
              Storage Analytics
            </button>
            <button
              onClick={() => setActiveTab('database')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'database'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Database className="w-4 h-4 inline mr-2" />
              Database Setup
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Site Settings
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-6">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-green-500">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <ImageIcon className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">Total Images</p>
                    <p className="text-lg font-bold text-gray-900">{images.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-blue-500">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Globe className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">CDN Enabled</p>
                    <p className="text-lg font-bold text-gray-900">{images.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-yellow-500">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Star className="w-4 h-4 text-yellow-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">Featured</p>
                    <p className="text-lg font-bold text-gray-900">{images.filter(img => img.featured).length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-purple-500">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Zap className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">Optimized</p>
                    <p className="text-lg font-bold text-gray-900">{images.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Vercel Blob Benefits Banner */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-start">
                <Cloud className="h-8 w-8 text-green-600 mr-4 mt-1" />
                <div className="flex-1">
                  <h3 className="font-semibold text-green-900 mb-2">Vercel Blob Storage Benefits</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center text-green-800">
                      <Globe className="w-4 h-4 mr-2" />
                      Global CDN with 100+ edge locations
                    </div>
                    <div className="flex items-center text-green-800">
                      <Zap className="w-4 h-4 mr-2" />
                      Zero cold starts & instant serving
                    </div>
                    <div className="flex items-center text-green-800">
                      <DollarSign className="w-4 h-4 mr-2" />
                      5GB free + pay-per-use pricing
                    </div>
                    <div className="flex items-center text-green-800">
                      <Shield className="w-4 h-4 mr-2" />
                      Enterprise security & 99.9% uptime
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Vercel Blob Images</h3>
                  <p className="text-sm text-gray-600">Manage all images stored in Vercel Blob with global CDN delivery</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={testFirebaseConnection}
                    disabled={testingFirebase}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center disabled:opacity-50"
                  >
                    <Database className={`w-4 h-4 mr-2 ${testingFirebase ? 'animate-spin' : ''}`} />
                    Test Firebase
                  </button>
                  <button
                    onClick={() => {
                      loadImages();
                      loadStorageStats();
                    }}
                    disabled={loadingImages}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingImages ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>
            
            {loadingImages ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                <p className="mt-2 text-gray-600">Loading Vercel Blob images...</p>
              </div>
            ) : images.length === 0 ? (
              <div className="text-center py-12">
                <Cloud className="w-12 h-12 mx-auto text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No Vercel Blob images found</h3>
                <p className="mt-1 text-sm text-gray-500">Upload images to Vercel Blob for global CDN delivery.</p>
                <button
                  onClick={() => setShowUploader(true)}
                  className="mt-4 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center mx-auto"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload First Image
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {images.map((image) => (
                  <VercelProductCard
                    key={image.id}
                    image={image}
                    onDelete={() => handleDeleteImage(image)}
                    onRefresh={loadImages}
                  />
                ))}
              </div>
            )}
            </div>
          </div>
        )}

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Upload to Vercel Blob</h3>
              <p className="text-sm text-gray-600">Upload images with global CDN delivery and automatic optimization</p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-dashed border-green-300 rounded-lg p-8 text-center">
              <Cloud className="w-16 h-16 mx-auto text-green-500 mb-4" />
              <h4 className="text-xl font-medium text-gray-900 mb-2">Vercel Blob Upload</h4>
              <p className="text-gray-600 mb-6">Images will be delivered via global CDN with automatic optimization</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-sm">
                <div className="flex items-center justify-center text-green-700">
                  <Globe className="w-4 h-4 mr-2" />
                  100+ Global Edge Locations
                </div>
                <div className="flex items-center justify-center text-green-700">
                  <Zap className="w-4 h-4 mr-2" />
                  Instant Image Serving
                </div>
                <div className="flex items-center justify-center text-green-700">
                  <Shield className="w-4 h-4 mr-2" />
                  Enterprise-Grade Security
                </div>
                <div className="flex items-center justify-center text-green-700">
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Automatic Optimization
                </div>
              </div>

              <button
                onClick={() => setShowUploader(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-medium flex items-center mx-auto"
              >
                <Cloud className="w-5 h-5 mr-3" />
                Upload to Vercel Blob
              </button>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Vercel Blob Analytics</h3>
              
              {storageStats ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <ImageIcon className="w-8 h-8 text-green-600 mr-3" />
                      <div>
                        <h4 className="text-lg font-semibold text-green-900">Total Images</h4>
                        <p className="text-2xl font-bold text-green-700">{storageStats.totalImages}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <Database className="w-8 h-8 text-blue-600 mr-3" />
                      <div>
                        <h4 className="text-lg font-semibold text-blue-900">Total Size</h4>
                        <p className="text-2xl font-bold text-blue-700">{formatBytes(storageStats.totalSize)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <Star className="w-8 h-8 text-purple-600 mr-3" />
                      <div>
                        <h4 className="text-lg font-semibold text-purple-900">With Metadata</h4>
                        <p className="text-2xl font-bold text-purple-700">{storageStats.withMetadata}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <Zap className="w-8 h-8 text-orange-600 mr-3" />
                      <div>
                        <h4 className="text-lg font-semibold text-orange-900">Avg Size</h4>
                        <p className="text-2xl font-bold text-orange-700">{formatBytes(storageStats.averageSize)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                  <p className="mt-2 text-gray-600">Loading analytics...</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Database Setup Tab */}
        {activeTab === 'database' && (
          <QuickDbSetup />
        )}

        {/* Site Settings Tab */}
        {activeTab === 'settings' && (
          <AdminSettings />
        )}
      </div>

      {/* Vercel Blob Uploader Modal */}
      {showUploader && (
        <VercelBlobUploader
          onClose={() => setShowUploader(false)}
          onUploadComplete={(results) => {
            console.log('Vercel Blob upload completed:', results);
            setShowUploader(false);
            loadImages();
            loadStorageStats();
          }}
        />
      )}
    </div>
  );
};

// Vercel Product Card Component
const VercelProductCard = ({ image, onDelete, onRefresh }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({
    title: image.title || '',
    description: image.description || '',
    price: image.price || '',
    category: image.category || 'Components',
    inStock: image.inStock !== undefined ? image.inStock : true,
    featured: image.featured || false
  });

  const handleSave = async () => {
    try {
      setSaving(true);
      console.log('💾 Saving Vercel Blob product data to Firebase...');
      console.log('📝 Image name:', image.name);
      console.log('📝 Edit data:', editData);

      // Validate required fields
      if (!editData.title.trim()) {
        alert('❌ Title is required');
        return;
      }

      // Save to Firebase with all metadata
      const metadataToSave = {
        title: editData.title.trim(),
        description: editData.description.trim(),
        category: editData.category.trim(),
        price: editData.price ? parseFloat(editData.price) : null,
        currency: 'K',
        inStock: editData.inStock,
        featured: editData.featured,
        storage_provider: 'vercel-blob',
        public_url: image.url,
        file_path: image.pathname,
        publicUrl: image.url,
        fullPath: image.pathname,
        // Vercel Blob specific fields
        vercel_blob_url: image.url,
        vercel_blob_pathname: image.pathname,
        vercel_blob_download_url: image.downloadUrl,
        vercel_blob_size: image.size,
        vercel_blob_uploaded_at: image.uploadedAt
      };

      console.log('📤 Sending to Firebase:', metadataToSave);

      const result = await firebaseMetadataService.saveImageMetadata(image.name, metadataToSave);

      console.log('✅ Firebase save result:', result);
      console.log('✅ Vercel Blob product data saved successfully to Firebase');

      // Show success message
      alert('✅ Product data saved successfully!');

      // Trigger refresh across all pages
      if (onRefresh) {
        await onRefresh();
      }

      // Trigger refresh on other pages
      if (window.refreshHomeImages) {
        window.refreshHomeImages();
      }
      if (window.refreshGalleryImages) {
        window.refreshGalleryImages();
      }

      // Dispatch global refresh event
      window.dispatchEvent(new CustomEvent('refreshAllImages'));

      setIsEditing(false);
    } catch (error) {
      console.error('❌ Error saving Vercel Blob product data:', error);
      alert(`❌ Failed to save changes: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      {/* Image */}
      <div className="aspect-square bg-gray-100 relative">
        <img
          src={image.url}
          alt={image.title || image.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = '/api/placeholder/200/200';
          }}
        />

        {/* Vercel Blob Badge */}
        <div className="absolute top-2 left-2">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <Cloud className="w-3 h-3 mr-1" />
            Vercel Blob
          </span>
        </div>

        {/* CDN Badge */}
        <div className="absolute top-2 right-2">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Globe className="w-3 h-3 mr-1" />
            CDN
          </span>
        </div>

        {/* Featured Badge */}
        {image.featured && (
          <div className="absolute bottom-2 left-2">
            <Star className="w-5 h-5 text-yellow-500 fill-current" />
          </div>
        )}

        {/* Actions */}
        <div className="absolute bottom-2 right-2 flex space-x-1">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="bg-white bg-opacity-90 hover:bg-opacity-100 p-1.5 rounded-full shadow-sm"
            title="Edit"
          >
            <Edit className="w-3 h-3 text-gray-600" />
          </button>
          <button
            onClick={onDelete}
            className="bg-white bg-opacity-90 hover:bg-opacity-100 p-1.5 rounded-full shadow-sm"
            title="Delete"
          >
            <X className="w-3 h-3 text-red-600" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        {isEditing ? (
          <div className="space-y-2">
            <input
              type="text"
              value={editData.title}
              onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Product title"
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-green-500 focus:border-transparent"
            />
            <textarea
              value={editData.description}
              onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Description"
              rows="2"
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-green-500 focus:border-transparent"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                value={editData.price}
                onChange={(e) => setEditData(prev => ({ ...prev, price: e.target.value }))}
                placeholder="Price (K)"
                className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-green-500 focus:border-transparent"
              />
              <select
                value={editData.category}
                onChange={(e) => setEditData(prev => ({ ...prev, category: e.target.value }))}
                className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-green-500 focus:border-transparent"
              >
                <option value="Components">Components</option>
                <option value="Arduino & Development Boards">Arduino & Development Boards</option>
                <option value="Sensors & Modules">Sensors & Modules</option>
                <option value="Display & Interface Modules">Display & Interface Modules</option>
                <option value="Motors & Motor Drivers">Motors & Motor Drivers</option>
                <option value="Other Components">Other Components</option>
              </select>
            </div>
            <div className="flex items-center space-x-4 text-sm">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={editData.inStock}
                  onChange={(e) => setEditData(prev => ({ ...prev, inStock: e.target.checked }))}
                  className="mr-1"
                />
                In Stock
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={editData.featured}
                  onChange={(e) => setEditData(prev => ({ ...prev, featured: e.target.checked }))}
                  className="mr-1"
                />
                Featured
              </label>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm disabled:opacity-50"
              >
                {saving ? <RefreshCw className="w-3 h-3 animate-spin mx-auto" /> : 'Save'}
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-3 py-1.5 rounded text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <h4 className="font-medium text-gray-900 text-sm mb-1 line-clamp-2">
              {image.title || image.name}
            </h4>
            {image.description && (
              <p className="text-xs text-gray-600 mb-2 line-clamp-2">{image.description}</p>
            )}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                {image.price && (
                  <span className="text-sm font-medium text-green-600">K{image.price}</span>
                )}
                {image.inStock ? (
                  <span className="text-xs text-green-600">In Stock</span>
                ) : (
                  <span className="text-xs text-red-600">Out of Stock</span>
                )}
              </div>
              <div className="flex items-center space-x-1">
                <Cloud className="w-4 h-4 text-green-600" title="Vercel Blob" />
                <Globe className="w-4 h-4 text-blue-600" title="Global CDN" />
              </div>
            </div>
            {image.category && (
              <p className="text-xs text-gray-500">{image.category}</p>
            )}
            <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
              <span>CDN Optimized</span>
              <span>{image.size ? `${Math.round(image.size / 1024)}KB` : ''}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VercelSuperAdmin;
